import type { IPublicClientApplication, AccountInfo } from '@azure/msal-browser'
import { apiConfig } from '../authConfig'

// Silent token acquisition fails when the cached session has expired, or transiently when the
// hidden renewal iframe times out (`monitor_window_timeout`) even though the session is still
// good - this happens often enough (blocked third-party cookies, slow network) that treating it
// as "session is dead, reload the whole page" (loginRedirect) kicks the user out constantly.
// A popup re-uses the existing SSO session instead: it resolves near-instantly without visible
// interaction unless the session really is gone, and never navigates the app away.
//
// inFlightRequest dedupes concurrent callers (dashboard poll + report list can both hit this at
// once) so they share one popup instead of colliding with MSAL's "interaction already in progress".
let inFlightRequest: Promise<string> | null = null

export async function acquireApiToken(instance: IPublicClientApplication, account: AccountInfo): Promise<string> {
  try {
    const response = await instance.acquireTokenSilent({
      scopes: apiConfig.scopes,
      authority: apiConfig.authority,
      account,
    })
    return response.accessToken
  } catch (error) {
    console.warn('Silent token acquisition failed, retrying via popup:', error)

    if (!inFlightRequest) {
      inFlightRequest = instance
        .acquireTokenPopup({ scopes: apiConfig.scopes, authority: apiConfig.authority, account })
        .then(response => response.accessToken)
        .finally(() => {
          inFlightRequest = null
        })
    }

    return inFlightRequest
  }
}
